import { derived, get, readable, writable, type Readable } from 'svelte/store';

import type {
  ConfigAIAutoOpenMapping,
  ConfigAITriggerPair,
} from '$lib/robot-config/find-new-trigger-resources';

export interface AssistedSetupAutoTrigger {
  question: string;
  models: ConfigAITriggerPair[];
  fragmentIds: string[];
}

export interface AssistedSetupTutorial {
  id: string;
  name: string;
  description: string;
  shortDescription: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  requirements: {
    hardware: string[];
    optional?: string[];
  };
  features: string[];
  assistedSetupMessage: string;
  skillReference: string;
  coverImage: string | null;
  author: string;
  version: string;
  lastUpdated: string;
  autoTriggers: AssistedSetupAutoTrigger[];
}

/** Query param used to deeplink into an assisted setup guide. */
export const ASSISTED_SETUP_QUERY_PARAM = 'assisted-setup';

/** Public GCS bucket hosting skill-index + skill trees (same as Go RemoteSkillLoader). */
export const ASSISTANT_ASSETS_BUCKET = 'ai-assistant-assets';
export const ASSISTANT_ASSETS_PUBLIC_BASE = `https://storage.googleapis.com/${ASSISTANT_ASSETS_BUCKET}`;
export const ASSISTANT_ASSETS_STAGING_ENV = 'staging';
const ASSISTANT_ASSETS_PUBLISH_MARKER = '.synced';

const GCS_LIST_API = `https://storage.googleapis.com/storage/v1/b/${ASSISTANT_ASSETS_BUCKET}/o`;

const gcsObjectMetadataURL = (objectName: string): string =>
  `${GCS_LIST_API}/${encodeURIComponent(objectName)}`;

const gcsObjectMediaURL = (objectName: string): string =>
  `${gcsObjectMetadataURL(objectName)}?alt=media`;

export type AssistedSetupCatalogStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface AssistedSetupCatalogState {
  status: AssistedSetupCatalogStatus;
  error?: string;
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

const parseTriggerPair = (value: unknown): ConfigAITriggerPair | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (!isNonEmptyString(record.api) || !isNonEmptyString(record.model)) {
    return undefined;
  }
  return { api: record.api.trim(), model: record.model.trim() };
};

const parseAutoTrigger = (
  value: unknown
): AssistedSetupAutoTrigger | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (!isNonEmptyString(record.question)) {
    return undefined;
  }

  const models: ConfigAITriggerPair[] = [];
  if (Array.isArray(record.models)) {
    for (const item of record.models) {
      const pair = parseTriggerPair(item);
      if (pair !== undefined) {
        models.push(pair);
      }
    }
  }

  const fragmentIds: string[] = [];
  if (Array.isArray(record.fragment_ids)) {
    for (const item of record.fragment_ids) {
      if (typeof item === 'string' && item !== '') {
        fragmentIds.push(item);
      }
    }
  }

  if (models.length === 0 && fragmentIds.length === 0) {
    return undefined;
  }

  return {
    question: record.question.trim(),
    models,
    fragmentIds,
  };
};

export const parseTutorial = (
  value: unknown
): AssistedSetupTutorial | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (
    !isNonEmptyString(record.id) ||
    !isNonEmptyString(record.name) ||
    !isNonEmptyString(record.skillReference) ||
    !isNonEmptyString(record.assistedSetupMessage)
  ) {
    return undefined;
  }

  const autoTriggers: AssistedSetupAutoTrigger[] = [];
  if (Array.isArray(record.auto_triggers)) {
    for (const item of record.auto_triggers) {
      const trigger = parseAutoTrigger(item);
      if (trigger !== undefined) {
        autoTriggers.push(trigger);
      }
    }
  }

  const requirements = record.requirements as
    | AssistedSetupTutorial['requirements']
    | undefined;

  return {
    id: record.id.trim(),
    name: record.name.trim(),
    description:
      typeof record.description === 'string' ? record.description : '',
    shortDescription:
      typeof record.shortDescription === 'string'
        ? record.shortDescription
        : '',
    difficulty:
      record.difficulty === 'beginner' ||
      record.difficulty === 'intermediate' ||
      record.difficulty === 'advanced'
        ? record.difficulty
        : 'intermediate',
    estimatedTime:
      typeof record.estimatedTime === 'string' ? record.estimatedTime : '',
    requirements: {
      hardware: Array.isArray(requirements?.hardware)
        ? requirements.hardware.filter(
            (item): item is string => typeof item === 'string'
          )
        : [],
      optional: Array.isArray(requirements?.optional)
        ? requirements.optional.filter(
            (item): item is string => typeof item === 'string'
          )
        : undefined,
    },
    features: Array.isArray(record.features)
      ? record.features.filter(
          (item): item is string => typeof item === 'string'
        )
      : [],
    assistedSetupMessage: record.assistedSetupMessage.trim(),
    skillReference: record.skillReference.trim(),
    coverImage:
      typeof record.coverImage === 'string' ? record.coverImage : null,
    author: typeof record.author === 'string' ? record.author : '',
    version: typeof record.version === 'string' ? record.version : '',
    lastUpdated:
      typeof record.lastUpdated === 'string' ? record.lastUpdated : '',
    autoTriggers,
  };
};

/** Mirror Go AssistantAssetsEnv: prod / staging / pr-N from env + hostname. */
export const resolveAssistantAssetsEnv = (
  environmentType: number | undefined,
  hostHint = ''
): string => {
  const hint = hostHint.trim().toLowerCase();
  const prMatch = /(?:^|[/.])pr-(?<pr>\d+)/u.exec(hint);

  // Matches environment.v1.EnvironmentType numeric values.
  switch (environmentType) {
    case 5: {
      return 'prod';
    }
    case 3: {
      return ASSISTANT_ASSETS_STAGING_ENV;
    }
    case 4: {
      return prMatch?.groups?.pr
        ? `pr-${prMatch.groups.pr}`
        : ASSISTANT_ASSETS_STAGING_ENV;
    }
    default: {
      return ASSISTANT_ASSETS_STAGING_ENV;
    }
  }
};

const tutorialsStore = writable<AssistedSetupTutorial[]>([]);
const catalogStateStore = writable<AssistedSetupCatalogState>({
  status: 'idle',
});

let loadPromise: Promise<AssistedSetupTutorial[]> | undefined;
let loadKey: string | undefined;

/** Reactive catalog used by browse UI, auto-open, and deeplinks. */
export const assistedSetupTutorialsStore: Readable<AssistedSetupTutorial[]> =
  readable<AssistedSetupTutorial[]>([], (set) => {
    const unsubscribe = tutorialsStore.subscribe(set);
    return unsubscribe;
  });

/** Load lifecycle for consumers that must wait before acting on catalog data. */
export const assistedSetupCatalogState: Readable<AssistedSetupCatalogState> =
  readable<AssistedSetupCatalogState>({ status: 'idle' }, (set) => {
    const unsubscribe = catalogStateStore.subscribe(set);
    return unsubscribe;
  });

export const getAllTutorials = (
  source: AssistedSetupTutorial[] = get(tutorialsStore)
): AssistedSetupTutorial[] => source;

export const getTutorialById = (
  id: string,
  source: AssistedSetupTutorial[] = get(tutorialsStore)
): AssistedSetupTutorial | undefined =>
  source.find((tutorial) => tutorial.id === id);

/** Resolve a deeplink ref by catalog id or skillReference (e.g. ?assisted-setup=ufactory-pick-and-place). */
export const getTutorialByGuideRef = (
  guideRef: string,
  source: AssistedSetupTutorial[] = get(tutorialsStore)
): AssistedSetupTutorial | undefined => {
  const trimmed = guideRef.trim();
  if (trimmed === '') {
    return undefined;
  }
  return (
    source.find((tutorial) => tutorial.id === trimmed) ??
    source.find((tutorial) => tutorial.skillReference === trimmed)
  );
};

/** Flatten each setup's auto_triggers into skill mappings; tutorial then trigger order is priority. */
export const getAutoOpenMappings = (
  source: AssistedSetupTutorial[] = get(tutorialsStore)
): ConfigAIAutoOpenMapping[] => {
  const mappings: ConfigAIAutoOpenMapping[] = [];
  for (const tutorial of source) {
    for (const trigger of tutorial.autoTriggers) {
      mappings.push({
        skill: tutorial.skillReference,
        name: tutorial.name,
        question: trigger.question,
        models: trigger.models,
        fragmentIds: trigger.fragmentIds,
      });
    }
  }
  return mappings;
};

export const searchTutorials = (
  searchTerm: string,
  source: AssistedSetupTutorial[] = get(tutorialsStore)
): AssistedSetupTutorial[] => {
  if (!searchTerm.trim()) {
    return source;
  }

  const term = searchTerm.toLowerCase().trim();
  const terms = term.split(/\s+/u);

  return source.filter((tutorial) => {
    const searchableText = [
      tutorial.name,
      tutorial.description,
      tutorial.shortDescription,
      ...tutorial.features,
      ...tutorial.requirements.hardware,
      ...(tutorial.requirements.optional ?? []),
    ]
      .join(' ')
      .toLowerCase();

    return terms.every((searchWord) => searchableText.includes(searchWord));
  });
};

export const createAssistedSetupSearchStore = (
  searchTerm: Readable<string>
): Readable<AssistedSetupTutorial[]> => {
  return derived([searchTerm, tutorialsStore], ([$searchTerm, $tutorials]) =>
    searchTutorials($searchTerm, $tutorials)
  );
};

const envPublished = async (env: string): Promise<boolean> => {
  try {
    const response = await fetch(
      gcsObjectMetadataURL(`${env}/${ASSISTANT_ASSETS_PUBLISH_MARKER}`),
      { method: 'GET', cache: 'no-cache' }
    );
    return response.ok;
  } catch {
    return false;
  }
};

const listSkillIndexObjects = async (env: string): Promise<string[]> => {
  const objectPrefix = `${env}/skill-index/`;

  const listPage = async (
    pageToken: string | undefined,
    names: string[]
  ): Promise<string[]> => {
    const url = new URL(GCS_LIST_API);
    url.searchParams.set('prefix', objectPrefix);
    url.searchParams.set('fields', 'items(name),nextPageToken');
    if (pageToken) {
      url.searchParams.set('pageToken', pageToken);
    }
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `listing skill-index under ${objectPrefix}: HTTP ${response.status}`
      );
    }
    const body = (await response.json()) as {
      items?: { name?: string }[];
      nextPageToken?: string;
    };
    const pageNames: string[] = [];
    for (const item of body.items ?? []) {
      if (item.name?.toLowerCase().endsWith('.json')) {
        pageNames.push(item.name);
      }
    }
    const merged = [...names, ...pageNames];
    if (!body.nextPageToken) {
      return merged;
    }
    return listPage(body.nextPageToken, merged);
  };

  return listPage(undefined, []);
};

const fetchTutorialsForEnv = async (
  env: string
): Promise<AssistedSetupTutorial[]> => {
  const objectNames = await listSkillIndexObjects(env);
  const tutorials: AssistedSetupTutorial[] = [];
  await Promise.all(
    objectNames.map(async (objectName) => {
      try {
        const response = await fetch(gcsObjectMediaURL(objectName), {
          cache: 'no-cache',
        });
        if (!response.ok) {
          console.error(
            `skipping skill-index object ${objectName}: HTTP ${response.status}`
          );
          return;
        }
        let raw: unknown;
        try {
          raw = await response.json();
        } catch (error) {
          console.error(
            `skipping unparseable skill-index object ${objectName}`,
            error
          );
          return;
        }
        const tutorial = parseTutorial(raw);
        if (tutorial) {
          tutorials.push(tutorial);
        } else {
          console.error(
            `skipping invalid skill-index object ${objectName}: failed validation`
          );
        }
      } catch (error) {
        console.error(
          `skipping skill-index object ${objectName} after fetch error`,
          error
        );
      }
    })
  );
  tutorials.sort((left, right) => left.id.localeCompare(right.id));
  return tutorials;
};

/** Resolve preferred → staging fallback (same as Go assetStore.resolveEnv). */
export const resolvePublishedAssetsEnv = async (
  preferredEnv: string
): Promise<string> => {
  if (await envPublished(preferredEnv)) {
    return preferredEnv;
  }
  if (
    preferredEnv !== ASSISTANT_ASSETS_STAGING_ENV &&
    (await envPublished(ASSISTANT_ASSETS_STAGING_ENV))
  ) {
    return ASSISTANT_ASSETS_STAGING_ENV;
  }
  return preferredEnv;
};

const loadCatalogWithFallback = async (
  preferredEnv: string
): Promise<AssistedSetupTutorial[]> => {
  const resolvedEnv = await resolvePublishedAssetsEnv(preferredEnv);
  let tutorials = await fetchTutorialsForEnv(resolvedEnv);
  if (
    tutorials.length === 0 &&
    resolvedEnv !== ASSISTANT_ASSETS_STAGING_ENV &&
    preferredEnv !== ASSISTANT_ASSETS_STAGING_ENV
  ) {
    const fallbackEnv = await resolvePublishedAssetsEnv(
      ASSISTANT_ASSETS_STAGING_ENV
    );
    if (fallbackEnv !== resolvedEnv) {
      tutorials = await fetchTutorialsForEnv(fallbackEnv);
    }
  }
  return tutorials;
};

export interface LoadAssistedSetupCatalogOptions {
  environmentType: number | undefined;
  /** Prefer appBaseUrl from GetEnvironment; falls back to window.location.hostname. */
  appBaseUrl?: string;
  hostname?: string;
  /** Bypass in-flight dedupe (tests). */
  force?: boolean;
}

/**
 * Load browse/auto-open catalog from public GCS skill-index JSON.
 * Dedupes concurrent calls for the same env key.
 */
export const ensureAssistedSetupCatalog = async (
  options: LoadAssistedSetupCatalogOptions
): Promise<AssistedSetupTutorial[]> => {
  const hostHint =
    options.appBaseUrl?.trim() ??
    options.hostname?.trim() ??
    (typeof window === 'undefined' ? '' : window.location.hostname);
  const preferredEnv = resolveAssistantAssetsEnv(
    options.environmentType,
    hostHint
  );
  const key = `${preferredEnv}|${hostHint}`;
  if (!options.force && loadPromise && loadKey === key) {
    return loadPromise;
  }
  loadKey = key;
  catalogStateStore.set({ status: 'loading' });
  loadPromise = (async () => {
    const tutorials = await loadCatalogWithFallback(preferredEnv);
    tutorialsStore.set(tutorials);
    catalogStateStore.set({ status: 'ready' });
    return tutorials;
  })();
  try {
    return await loadPromise;
  } catch (error) {
    loadPromise = undefined;
    loadKey = undefined;
    const message = error instanceof Error ? error.message : String(error);
    catalogStateStore.set({ status: 'error', error: message });
    throw error;
  }
};

/** Test helper: seed the catalog without hitting GCS. */
export const setAssistedSetupCatalogForTests = (
  tutorials: AssistedSetupTutorial[]
): void => {
  const sorted = [...tutorials].sort((left, right) =>
    left.id.localeCompare(right.id)
  );
  loadPromise = Promise.resolve(sorted);
  loadKey = 'test';
  tutorialsStore.set(sorted);
  catalogStateStore.set({ status: 'ready' });
};

/** Test helper: reset loader state between specs. */
export const resetAssistedSetupCatalogForTests = (): void => {
  loadPromise = undefined;
  loadKey = undefined;
  tutorialsStore.set([]);
  catalogStateStore.set({ status: 'idle' });
};
